import { useState, useMemo } from 'react';
import { Box, Flex, Paragraph, Button, Image } from 'theme-ui';
import { useProductMetafield, useSettings } from '@backpackjs/storefront';
import { Link } from '@snippets';

import { StoreLocator } from './StoreLocator';
import { themed } from './Metadata.theme';

export const Metadata = themed(({ theme, product, selectedVariant }) => {
  const { metafield } = useProductMetafield({
    handle: product?.handle,
    namespace: 'my_fields',
    key: 'specs_and_fitment',
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [locallyInjected, setLocallyInjected] = useState(false);

  const settings = useSettings();
  const guides = settings?.product?.specs?.guides;

  const usageGuide = useMemo(() => {
    if (!product?.tags?.length || !guides?.length) return;

    const productTag = product?.tags?.find((tag) =>
      tag.startsWith('usage-guide::')
    );

    const productGuide = guides?.find(
      (guide) =>
        guide?.tag?.toLowerCase() ===
        productTag?.split('usage-guide::')?.[1]?.toLowerCase().trim()
    );

    return productGuide;
  }, [product?.id, guides]);

  const tabs = [
    {
      title: 'Details',
      content: (
        <Box
          sx={{
            width: '100%',
            py: 8,
            borderBottomStyle: product?.descriptionHtml ? 'solid' : 'none',
            borderBottomWidth: '1px',
            borderBottomColor: 'gray',
            'ul, ol': { pl: 0 },
            'p, span': {
              variant: 'text.base',
              mt: 0,
            },
            'p:last-of-type': {
              mb: 0,
            },
          }}
          dangerouslySetInnerHTML={{ __html: product?.descriptionHtml }}
        />
      ),
    },
    {
      title: 'Specs & Fitment',
      content: (
        <>
          <Paragraph
            sx={{
              py: 8,
              fontSize: [3, 4],
              borderBottomStyle:
                metafield?.value && !usageGuide ? 'solid' : 'none',
              borderBottomWidth: '1px',
              borderBottomColor: 'gray',
              p: { py: 0 },
            }}
            dangerouslySetInnerHTML={{
              __html: metafield?.value?.replaceAll('\n', '<br>'),
            }}
          />
          {usageGuide && (
            <Flex
              sx={{
                variant: 'flex.row.start.center',
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
                borderBottomColor: 'gray',
                py: '16px',
              }}
            >
              <Image
                alt={usageGuide.tag}
                src={'/svgs/pdf-logo.svg' || '/svgs/default-image.svg'}
                sx={{ width: '30px', mr: 3 }}
                loading="lazy"
              />
              <Link
                variant="buttons.link.1"
                href={usageGuide?.cta?.url}
                newTab={usageGuide?.cta?.newTab}
              >
                <Paragraph
                  sx={{
                    lineHeight: 18,
                    fontWeight: 500,
                    fontSize: [3, 4],
                  }}
                >
                  {usageGuide?.cta?.text}
                </Paragraph>
              </Link>
            </Flex>
          )}
        </>
      ),
    },
    {
      title: 'Find a Local Dealer',
      content: (
        <StoreLocator
          selectedVariant={selectedVariant}
          locallyInjected={locallyInjected}
          setLocallyInjected={setLocallyInjected}
        />
      ),
    },
  ];

  return (
    <Box data-comp={Metadata.displayName} sx={theme.wrapper}>
      <Flex sx={theme.tabs}>
        {tabs.map(({ title }, index) => {
          const isActive = index === activeIndex;
          return (
            <Button
              key={title}
              variant="buttons.plain"
              onClick={() => setActiveIndex(index)}
              sx={theme.tab(isActive)}
            >
              <Paragraph sx={theme.tabTitle}>{title}</Paragraph>
            </Button>
          );
        })}
      </Flex>

      {tabs.map(({ title, content }, index) => {
        const isActive = index === activeIndex;
        return (
          <Box key={title} sx={theme.content(isActive)}>
            {content || null}
          </Box>
        );
      })}
    </Box>
  );
});

Metadata.displayName = 'Metadata';
